import {
  adminSetPortalDisplayName,
  adminSetPortalRole,
  emailForPortalAccessId,
  fetchPortalAccessRemovals,
  fetchPortalMembersByKind,
  type PortalAccessKind,
} from './accessManagement';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase, type DbPortalKind } from './supabase';
import type { AllowedEmail } from '../types/crm';

export type AllowlistPortalKind = DbPortalKind | 'accounts' | 'report' | 'purchasing' | 'design';

function asPortalAccessKind(kind: AllowlistPortalKind): PortalAccessKind {
  return kind;
}

export interface PortalAllowedEmailRemoval {
  id: string;
  allowed_email_id?: string | null;
  email: string;
  display_name?: string | null;
  role: string;
  deleted_by: string;
  deleted_at?: string;
  reason?: string | null;
}

export function allowlistDbKind(kind: AllowlistPortalKind): DbPortalKind {
  if (kind === 'accounts' || kind === 'report' || kind === 'purchasing' || kind === 'design') return 'crm';
  return kind;
}

export function allowlistWorkspaceLabel(kind: AllowlistPortalKind): string {
  if (kind === 'it') return 'IT Management';
  if (kind === 'dem') return 'Data Entry (Web)';
  if (kind === 'forms') return 'Application Form';
  if (kind === 'accounts') return 'Accounts';
  if (kind === 'report') return 'Report';
  if (kind === 'purchasing') return 'Purchasing';
  if (kind === 'design') return 'Design';
  return 'Sales CRM';
}

export async function fetchPortalAllowedEmails(kind: AllowlistPortalKind): Promise<AllowedEmail[]> {
  const rows = await fetchPortalMembersByKind(asPortalAccessKind(kind));
  return rows.map((row) => ({
    id: row.id,
    email: row.email,
    display_name: row.display_name,
    role: row.role as AllowedEmail['role'],
    username: row.username,
    auth_id: row.auth_id,
    department_id: row.department_id,
    created_at: row.created_at,
  }));
}

export async function fetchPortalAllowedEmailRemovals(
  kind: AllowlistPortalKind,
): Promise<PortalAllowedEmailRemoval[]> {
  const rows = await fetchPortalAccessRemovals(asPortalAccessKind(kind), 50);
  return rows.map((row) => ({
    id: row.id,
    email: row.email,
    display_name: row.display_name,
    role: row.role ?? 'user',
    deleted_by: row.deleted_by ?? '',
    deleted_at: row.deleted_at,
    reason: row.reason,
  }));
}

export async function removePortalAllowedEmail(
  kind: AllowlistPortalKind,
  id: string,
  reason: string,
  options?: { skipOwnershipCheck?: boolean },
): Promise<void> {
  if (kind === 'crm' && !options?.skipOwnershipCheck) {
    await assertCrmAllowlistRemovable(id);
  }

  const email = await emailForPortalAccessId(id);
  if (!email) return;

  const wide = await adminSetPortalRole({
    email,
    portal: kind,
    role: null,
    reason: reason.trim(),
  });
  if (wide === 'ok') return;
  throw new Error(
    'access_management.admin_set_portal_role is missing. Run migration 258 in Supabase.',
  );
}

/** CRM members may still own clients/contacts/projects/tasks via soft username refs. */
export async function assertCrmAllowlistRemovable(id: string): Promise<void> {
  const db = getDbSupabase('crm');
  const members = await fetchPortalMembersByKind('crm');
  const row = members.find((m) => m.id === id);
  if (!row) return;

  const username = String(row.username ?? '').trim();
  if (!username) return;

  const tables = [
    { table: 'clients', label: 'account(s)' },
    { table: 'contacts', label: 'contact(s)' },
    { table: 'projects', label: 'project(s)' },
    { table: 'tasks', label: 'task(s)' },
  ] as const;
  const counts: { label: string; count: number }[] = [];
  for (const { table, label } of tables) {
    const { count, error } = await db
      .from(table)
      .select('id', { count: 'exact', head: true })
      .eq('owner_id', username);
    if (error) {
      // Table may be RLS-limited; fall through to delete and let FK message explain.
      if (error.code === '42501' || /permission denied|row-level security/i.test(error.message ?? '')) {
        return;
      }
      throw new Error(formatSupabaseError(error));
    }
    if ((count ?? 0) > 0) {
      counts.push({ label, count: count ?? 0 });
    }
  }

  const { count: updateCount, error: updateError } = await db
    .from('project_updates')
    .select('id', { count: 'exact', head: true })
    .eq('author_id', username);
  if (!updateError && (updateCount ?? 0) > 0) {
    counts.push({ label: 'project update(s)', count: updateCount ?? 0 });
  }

  if (counts.length === 0) return;

  const accountCount = counts.find((c) => c.label === 'account(s)')?.count ?? 0;
  if (accountCount > 0) {
    throw new Error(
      `This user still has ${accountCount} account(s). ` +
        'Transfer the accounts to another owner first, then change CRM access again.',
    );
  }

  const detail = counts.map((c) => `${c.count} ${c.label}`).join(', ');
  throw new Error(
    `This user still has ${detail}. ` +
      'Transfer or reassign those records to another owner first, then change CRM access again.',
  );
}

export async function updatePortalAllowedEmail(
  kind: AllowlistPortalKind,
  id: string,
  patch: { display_name?: string | null; role?: string; department_id?: string | null },
): Promise<void> {
  const email = await emailForPortalAccessId(id);
  if (!email) {
    throw new Error(`Allowlist entry not found for ${kind}.`);
  }

  if (patch.role !== undefined) {
    const wide = await adminSetPortalRole({
      email,
      portal: kind,
      role: patch.role,
      displayName: patch.display_name,
      departmentId: patch.department_id,
    });
    if (wide === 'ok') return;
    throw new Error(
      'access_management.admin_set_portal_role is missing. Run migration 258 in Supabase.',
    );
  }

  if (patch.display_name !== undefined) {
    const wide = await adminSetPortalDisplayName(email, patch.display_name ?? '');
    if (wide === 'ok') return;
    throw new Error(
      'access_management.admin_set_display_name is missing. Run migration 258 in Supabase.',
    );
  }
}

export async function addPortalAllowedEmail(
  kind: AllowlistPortalKind,
  input: {
    email: string;
    displayName?: string | null;
    role: string;
    departmentId?: string | null;
  },
): Promise<void> {
  const wide = await adminSetPortalRole({
    email: input.email,
    portal: kind,
    role: input.role,
    displayName: input.displayName,
    departmentId: input.departmentId,
  });
  if (wide === 'ok') return;
  throw new Error(
    'access_management.admin_set_portal_role is missing. Run migration 258 in Supabase.',
  );
}
